// src/constants/api.ts
var API_BASE_URL = "https://apilinkoo.creago.be/rest/linkoo";
var API_ENDPOINTS = {
  // Peoples
  PEOPLES: `${API_BASE_URL}/peoples`,
  // Contacts
  CONTACTS: `${API_BASE_URL}/contacts`,
  // Places
  PLACES: `${API_BASE_URL}/places`,
  // Events
  EVENTS: `${API_BASE_URL}/events`,
  // Auth
  USER_AUTH: `${API_BASE_URL}/userAuth`,
  // Files
  FILES: `${API_BASE_URL}/files`,
  // Occupations
  OCCUPATIONS: `${API_BASE_URL}/occupations`,
  // Skills
  SKILLS: `${API_BASE_URL}/skills`
};
var API_KEY = "linkoo_api_key_2024_change_me";

// src/utils/normalize.ts
var KNOWN_MAPPINGS = {
  "FIRSTNAME": "firstName",
  "LASTNAME": "lastName",
  "BORNDATE": "bornDate",
  "BORNPLACE": "bornPlace",
  "PHONEBUREAU": "phoneBureau",
  "TVANUM": "tvaNum",
  "BANKNAME": "bankName",
  "BANKNUM": "bankNum",
  "HOMENEEDS": "homeNeeds",
  "CREATEDAT": "createdAt",
  "UPDATEDAT": "updatedAt",
  "PEOPLEUUID": "peopleUuid",
  "EVENTUUID": "eventUuid",
  "LOCALUUID": "localuuid",
  "SUBSCRIPTIONTYPE": "subscriptionType",
  "SUBSCRIPTIONVALIDUNTIL": "subscriptionValidUntil",
  "MOLLIECUSTOMERID": "mollieCustomerId",
  "PINNEDITEMS": "pinnedItems",
  "PEOPLEID": "peopleId",
  "MODULEID": "moduleId",
  "ITEMID": "itemId",
  "ISCOLLAPSED": "isCollapsed",
  "GROUPID": "groupId",
  "SELECTEDTHEMEID": "selectedThemeId",
  "COLLAPSEDSIDEBARGROUPS": "collapsedSidebarGroups",
  "LANGUAGE": "language",
  "CREATEDBYID": "createdById",
  "OWNERID": "ownerId",
  "EVENTDATE": "eventDate",
  "EVENTTIME": "eventTime",
  "ENDDATE": "endDate",
  "ENDTIME": "endTime",
  "GOOGLEADDRESS": "googleAddress",
  "COUNTRYCODE": "countryCode",
  "MAXPARTICIPANTS": "maxParticipants",
  "CURRENTPARTICIPANTS": "currentParticipants",
  "ISPUBLIC": "isPublic",
  "OWNERNAME": "ownerName",
  "STORAGEKEY": "storageKey"
};
var toCamelCase = (str) => {
  const upper = str.toUpperCase();
  if (KNOWN_MAPPINGS[upper]) {
    return KNOWN_MAPPINGS[upper];
  }
  return str.toLowerCase();
};
var normalizeKeys = (obj) => {
  if (obj === null || obj === void 0) return obj;
  if (Array.isArray(obj)) {
    return obj.map((item) => normalizeKeys(item));
  }
  if (typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => {
        const newKey = toCamelCase(key);
        return [newKey, normalizeKeys(value)];
      })
    );
  }
  return obj;
};
var getValue = (obj, key, defaultValue) => {
  const value = obj[key] ?? obj[key.toUpperCase()] ?? obj[key.toLowerCase()];
  return value ?? defaultValue;
};

// src/services/apiClient.ts
var storageAdapter = {
  getItem: (key) => {
    if (typeof window !== "undefined" && window.localStorage) {
      return window.localStorage.getItem(key);
    }
    return null;
  },
  setItem: (key, value) => {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(key, value);
    }
  },
  removeItem: (key) => {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.removeItem(key);
    }
  }
};
var setStorageAdapter = (adapter) => {
  storageAdapter = adapter;
};
var getStorage = () => storageAdapter;
var STORAGE_KEY_TOKEN = "linkoo_jwt_token";
var STORAGE_KEY_API_KEY = "linkoo_api_key";
var getAuthToken = async () => {
  return storageAdapter.getItem(STORAGE_KEY_TOKEN);
};
var getApiKey = async () => {
  return storageAdapter.getItem(STORAGE_KEY_API_KEY);
};
var setAuthToken = async (token) => {
  return storageAdapter.setItem(STORAGE_KEY_TOKEN, token);
};
var removeAuthToken = async () => {
  return storageAdapter.removeItem(STORAGE_KEY_TOKEN);
};
var getAuthHeaders = async () => {
  const token = await getAuthToken();
  const apiKey = await getApiKey();
  const headers = {
    "Content-Type": "application/json",
    "Accept": "application/json"
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  } else if (apiKey) {
    headers["X-API-Key"] = apiKey;
  } else {
    headers["X-API-Key"] = API_KEY;
  }
  return headers;
};
var handleResponse = async (response) => {
  const responseText = await response.text();
  let result;
  try {
    result = JSON.parse(responseText);
  } catch (parseError) {
    throw new Error(`Invalid JSON response: ${responseText}`);
  }
  if (!response.ok) {
    const error = {
      status: response.status,
      message: result.message || result.MESSAGE || `HTTP error! status: ${response.status}`,
      data: result
    };
    throw error;
  }
  return result;
};
var apiRequest = async (url, options = {}) => {
  const headers = await getAuthHeaders();
  const response = await fetch(url, {
    ...options,
    headers: {
      ...headers,
      ...options.headers
    }
  });
  return handleResponse(response);
};
var apiGet = async (url) => {
  return apiRequest(url, { method: "GET" });
};
var apiPost = async (url, body) => {
  return apiRequest(url, {
    method: "POST",
    body: JSON.stringify(body)
  });
};
var apiPut = async (url, body) => {
  return apiRequest(url, {
    method: "PUT",
    body: JSON.stringify(body)
  });
};
var apiDelete = async (url) => {
  return apiRequest(url, { method: "DELETE" });
};

// src/services/authService.ts
var STORAGE_KEYS = {
  AUTH_TOKEN: "authToken",
  USER_ID: "userId",
  USER_UUID: "userUuid",
  USER_EMAIL: "userEmail",
  USER_NAME: "userName",
  USER_ROLE: "userRole",
  LINKOO_USER: "linkoo_user"
};
var register = async (input) => {
  try {
    const response = await fetch(`${API_ENDPOINTS.USER_AUTH}/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": API_KEY
      },
      body: JSON.stringify(input)
    });
    const result = await response.json();
    return {
      success: result.success || result.SUCCESS || false,
      message: result.message || result.MESSAGE,
      token: result.token,
      emailSent: result.emailSent
    };
  } catch (error) {
    console.error("Register error:", error);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Registration failed"
    };
  }
};
var login = async (credentials) => {
  try {
    const response = await fetch(`${API_ENDPOINTS.USER_AUTH}/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": API_KEY
      },
      body: JSON.stringify({
        email: credentials.email,
        code: credentials.code
      })
    });
    const result = await response.json();
    const success = result.success || result.SUCCESS || false;
    if (success && result.token) {
      const user = result.user || {};
      const storage = getStorage();
      await setAuthToken(result.token);
      await storage.setItem(STORAGE_KEYS.USER_ID, String(user.id || ""));
      await storage.setItem(STORAGE_KEYS.USER_UUID, user.peopleUuid || "");
      await storage.setItem(STORAGE_KEYS.USER_EMAIL, user.email || credentials.email);
      await storage.setItem(STORAGE_KEYS.USER_NAME, `${user.firstName || ""} ${user.lastName || ""}`.trim());
      await storage.setItem(STORAGE_KEYS.USER_ROLE, user.role || "");
      await storage.setItem(STORAGE_KEYS.LINKOO_USER, JSON.stringify(user));
      return {
        success: true,
        message: result.message || result.MESSAGE,
        token: result.token,
        user: {
          id: user.id,
          email: user.email || credentials.email,
          firstName: user.firstName,
          lastName: user.lastName,
          peopleUuid: user.peopleUuid,
          storageKey: user.storageKey,
          role: user.role
        }
      };
    }
    return {
      success: false,
      message: result.message || result.MESSAGE || "Login failed"
    };
  } catch (error) {
    console.error("Login error:", error);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Login failed"
    };
  }
};
var forgotCode = async (email) => {
  try {
    const response = await fetch(`${API_ENDPOINTS.USER_AUTH}/forgot-code`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": API_KEY
      },
      body: JSON.stringify({ email })
    });
    const result = await response.json();
    return {
      success: result.success || result.SUCCESS || false,
      message: result.message || result.MESSAGE,
      emailSent: result.emailSent
    };
  } catch (error) {
    console.error("Forgot code error:", error);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Request failed"
    };
  }
};
var logout = async () => {
  const storage = getStorage();
  await removeAuthToken();
  await storage.removeItem(STORAGE_KEYS.USER_ID);
  await storage.removeItem(STORAGE_KEYS.USER_UUID);
  await storage.removeItem(STORAGE_KEYS.USER_EMAIL);
  await storage.removeItem(STORAGE_KEYS.USER_NAME);
  await storage.removeItem(STORAGE_KEYS.USER_ROLE);
  await storage.removeItem(STORAGE_KEYS.LINKOO_USER);
};
var isAuthenticated = async () => {
  const token = await getAuthToken();
  return !!token;
};
var getCurrentUser = async () => {
  const storage = getStorage();
  const userJson = await storage.getItem(STORAGE_KEYS.LINKOO_USER);
  if (userJson) {
    try {
      return JSON.parse(userJson);
    } catch (e) {
      console.error("Error parsing user from storage:", e);
    }
  }
  const id = await storage.getItem(STORAGE_KEYS.USER_ID);
  const email = await storage.getItem(STORAGE_KEYS.USER_EMAIL);
  if (id && email) {
    return {
      id: parseInt(id, 10),
      email,
      firstName: void 0,
      lastName: void 0,
      peopleUuid: await storage.getItem(STORAGE_KEYS.USER_UUID) || void 0,
      role: await storage.getItem(STORAGE_KEYS.USER_ROLE) || void 0
    };
  }
  return null;
};

// src/services/peopleService.ts
var getAllPeople = async (options = {}) => {
  try {
    const {
      page = 1,
      limit = 100,
      sortBy = "createdAt",
      sortOrder = "DESC",
      keyword = "",
      search = ""
    } = options;
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sortBy,
      sortOrder
    });
    const searchTerm = keyword || search;
    if (searchTerm) {
      params.append("keyword", searchTerm);
    }
    const result = await apiGet(`${API_ENDPOINTS.PEOPLES}?${params}`);
    const data = result.data || result.DATA || [];
    return normalizeKeys(data);
  } catch (error) {
    console.error("[peopleService] Error fetching people:", error);
    throw error;
  }
};
var getPeopleByUuid = async (uuid) => {
  try {
    const result = await apiGet(`${API_ENDPOINTS.PEOPLES}/${uuid}`);
    const data = result.data || result.DATA;
    return data ? normalizeKeys(data) : null;
  } catch (error) {
    console.error("Error fetching people:", error);
    return null;
  }
};
var createPeople = async (peopleData) => {
  return apiPost(API_ENDPOINTS.PEOPLES, peopleData);
};
var updatePeople = async (uuid, peopleData) => {
  return apiPut(`${API_ENDPOINTS.PEOPLES}/${uuid}`, peopleData);
};
var deletePeople = async (uuid) => {
  return apiDelete(`${API_ENDPOINTS.PEOPLES}/${uuid}`);
};
var updatePeopleCoordinates = async (uuid, latitude, longitude) => {
  return apiPut(`${API_ENDPOINTS.PEOPLES}/${uuid}`, { latitude, longitude });
};
var getProfileStats = async (uuid) => {
  try {
    const result = await apiGet(`${API_ENDPOINTS.PEOPLES}/${uuid}/profile-stats`);
    const resultObj = result;
    const success = getValue(resultObj, "success", false);
    if (success) {
      const data = result.data || result.DATA;
      return {
        contactCount: getValue(data, "contactCount", 0),
        appointmentCount: getValue(data, "appointmentCount", 0),
        ownedEventCount: getValue(data, "ownedEventCount", 0),
        participatingEventCount: getValue(data, "participatingEventCount", 0)
      };
    }
    return null;
  } catch (error) {
    console.error("Error fetching profile stats:", error);
    return null;
  }
};
var getPeoplePreferences = async (peopleId) => {
  try {
    const result = await apiGet(`${API_ENDPOINTS.PEOPLES}/${peopleId}/preferences`);
    const data = result.data || result.DATA || {};
    return normalizeKeys(data);
  } catch (error) {
    console.error("Error fetching preferences:", error);
    return {};
  }
};
var savePeoplePreferences = async (peopleId, preferences) => {
  return apiPut(`${API_ENDPOINTS.PEOPLES}/${peopleId}/preferences`, preferences);
};
var getPersonByUuid = getPeopleByUuid;
var getPersonById = getPeopleByUuid;
var createPerson = createPeople;
var savePerson = createPeople;
var updatePerson = updatePeople;
var deletePerson = deletePeople;
var updatePersonCoordinates = updatePeopleCoordinates;

// src/services/eventsService.ts
var EUROPEAN_COUNTRIES = [
  { code: "BE", name: "Belgique", nameEn: "Belgium" },
  { code: "FR", name: "France", nameEn: "France" },
  { code: "DE", name: "Allemagne", nameEn: "Germany" },
  { code: "NL", name: "Pays-Bas", nameEn: "Netherlands" },
  { code: "LU", name: "Luxembourg", nameEn: "Luxembourg" },
  { code: "CH", name: "Suisse", nameEn: "Switzerland" },
  { code: "IT", name: "Italie", nameEn: "Italy" },
  { code: "ES", name: "Espagne", nameEn: "Spain" },
  { code: "PT", name: "Portugal", nameEn: "Portugal" },
  { code: "GB", name: "Royaume-Uni", nameEn: "United Kingdom" },
  { code: "IE", name: "Irlande", nameEn: "Ireland" },
  { code: "AT", name: "Autriche", nameEn: "Austria" },
  { code: "PL", name: "Pologne", nameEn: "Poland" },
  { code: "CZ", name: "Tch\xE9quie", nameEn: "Czech Republic" },
  { code: "SK", name: "Slovaquie", nameEn: "Slovakia" },
  { code: "HU", name: "Hongrie", nameEn: "Hungary" },
  { code: "RO", name: "Roumanie", nameEn: "Romania" },
  { code: "BG", name: "Bulgarie", nameEn: "Bulgaria" },
  { code: "GR", name: "Gr\xE8ce", nameEn: "Greece" },
  { code: "HR", name: "Croatie", nameEn: "Croatia" },
  { code: "SI", name: "Slov\xE9nie", nameEn: "Slovenia" },
  { code: "DK", name: "Danemark", nameEn: "Denmark" },
  { code: "SE", name: "Su\xE8de", nameEn: "Sweden" },
  { code: "NO", name: "Norv\xE8ge", nameEn: "Norway" },
  { code: "FI", name: "Finlande", nameEn: "Finland" },
  { code: "EE", name: "Estonie", nameEn: "Estonia" },
  { code: "LV", name: "Lettonie", nameEn: "Latvia" },
  { code: "LT", name: "Lituanie", nameEn: "Lithuania" }
];
var extractCountryFromPlace = (addressComponents) => {
  if (!addressComponents || !Array.isArray(addressComponents)) {
    return { country: "", countryCode: "" };
  }
  const countryComponent = addressComponents.find(
    (comp) => comp.types && comp.types.includes("country")
  );
  if (countryComponent) {
    return {
      country: countryComponent.long_name || "",
      countryCode: countryComponent.short_name || ""
    };
  }
  return { country: "", countryCode: "" };
};
var normalizeEvent = (event) => ({
  id: event.id || event.ID,
  eventUuid: event.eventUuid || event.EVENTUUID || event.eventuuid,
  creatorId: event.creatorId || event.CREATORID || event.creatorid,
  creatorFirstName: event.creatorFirstName || event.CREATORFIRSTNAME || event.creatorfirstname || "",
  creatorLastName: event.creatorLastName || event.CREATORLASTNAME || event.creatorlastname || "",
  creatorUuid: event.creatorUuid || event.CREATORUUID || event.creatoruuid || "",
  title: event.title || event.TITLE || "",
  description: event.description || event.DESCRIPTION || "",
  eventDate: event.eventDate || event.EVENTDATE || event.eventdate,
  startTime: event.startTime || event.STARTTIME || event.starttime || "",
  endTime: event.endTime || event.ENDTIME || event.endtime || "",
  placeName: event.placeName || event.PLACENAME || event.placename || "",
  placeAddress: event.placeAddress || event.PLACEADDRESS || event.placeaddress || "",
  latitude: event.latitude || event.LATITUDE || null,
  longitude: event.longitude || event.LONGITUDE || null,
  country: event.country || event.COUNTRY || "",
  countryCode: event.countryCode || event.COUNTRYCODE || event.countrycode || "",
  maxParticipants: event.maxParticipants || event.MAXPARTICIPANTS || event.maxparticipants || 0,
  participantCount: event.participantCount || event.PARTICIPANTCOUNT || event.participantcount || 0,
  status: event.status || event.STATUS || "active",
  createdAt: event.createdAt || event.CREATEDAT || event.createdat
});
var normalizeParticipant = (p) => ({
  id: p.id || p.ID,
  eventId: p.eventId || p.EVENTID || p.eventid,
  peopleId: p.peopleId || p.PEOPLEID || p.peopleid,
  firstName: p.firstName || p.FIRSTNAME || p.firstname || "",
  lastName: p.lastName || p.LASTNAME || p.lastname || "",
  peopleUuid: p.peopleUuid || p.PEOPLEUUID || p.peopleuuid || "",
  status: p.status || p.STATUS || "confirmed",
  joinedAt: p.joinedAt || p.JOINEDAT || p.joinedat
});
var normalizeGroup = (g) => ({
  id: g.id || g.ID,
  eventId: g.eventId || g.EVENTID,
  groupId: g.groupId || g.GROUPID,
  groupName: g.groupName || g.GROUPNAME,
  groupUuid: g.groupUuid || g.GROUPUUID,
  memberCount: g.memberCount || g.MEMBERCOUNT || 0,
  createdAt: g.createdAt || g.CREATEDAT
});
var getEvents = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.countryCode) params.append("countryCode", filters.countryCode);
    if (filters.keyword) params.append("keyword", filters.keyword);
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.ownerId) params.append("ownerId", filters.ownerId.toString());
    if (filters.limit) params.append("limit", filters.limit.toString());
    if (filters.page) params.append("page", filters.page.toString());
    const url = `${API_ENDPOINTS.EVENTS}${params.toString() ? `?${params}` : ""}`;
    const result = await apiGet(url);
    const resultObj = result;
    const success = resultObj.success || resultObj.SUCCESS;
    const data = result.data || result.DATA || [];
    const total = resultObj.total || resultObj.TOTAL || data.length;
    if (success) {
      return {
        events: data.map(normalizeEvent),
        total
      };
    }
    return { events: [], total: 0 };
  } catch (error) {
    console.error("Error fetching events:", error);
    return { events: [], total: 0 };
  }
};
var getEventByUuid = async (eventUuid) => {
  try {
    const result = await apiGet(`${API_ENDPOINTS.EVENTS}/${eventUuid}`);
    const resultObj = result;
    const success = resultObj.success || resultObj.SUCCESS;
    const data = result.data || result.DATA;
    if (success && data) {
      return normalizeEvent(data);
    }
    return null;
  } catch (error) {
    console.error("Error fetching event:", error);
    return null;
  }
};
var getEventById = getEventByUuid;
var createEvent = async (eventData, creatorId) => {
  try {
    const result = await apiPost(API_ENDPOINTS.EVENTS, {
      ...eventData,
      creatorId,
      addToAgenda: true
    });
    const resultObj = result;
    const data = result.data || result.DATA || {};
    return {
      success: resultObj.success || resultObj.SUCCESS,
      eventUuid: data.eventUuid || data.EVENTUUID,
      eventId: data.eventId || data.EVENTID,
      message: resultObj.message || resultObj.MESSAGE || ""
    };
  } catch (error) {
    console.error("Error creating event:", error);
    return { success: false, message: error instanceof Error ? error.message : "Failed to create event" };
  }
};
var updateEvent = async (eventUuid, eventData) => {
  try {
    const result = await apiPut(`${API_ENDPOINTS.EVENTS}/${eventUuid}`, eventData);
    const resultObj = result;
    return {
      success: resultObj.success || resultObj.SUCCESS,
      message: resultObj.message || resultObj.MESSAGE || ""
    };
  } catch (error) {
    console.error("Error updating event:", error);
    return { success: false, message: error instanceof Error ? error.message : "Failed to update event" };
  }
};
var deleteEvent = async (eventUuid, creatorId) => {
  try {
    const result = await apiDelete(`${API_ENDPOINTS.EVENTS}/${eventUuid}?creatorId=${creatorId}`);
    const resultObj = result;
    return {
      success: resultObj.success || resultObj.SUCCESS,
      message: resultObj.message || resultObj.MESSAGE || ""
    };
  } catch (error) {
    console.error("Error deleting event:", error);
    return { success: false, message: error instanceof Error ? error.message : "Failed to delete event" };
  }
};
var getEventParticipants = async (eventId) => {
  try {
    const result = await apiGet(`${API_ENDPOINTS.EVENTS}/${eventId}/participants`);
    const resultObj = result;
    const success = resultObj.success || resultObj.SUCCESS;
    const data = result.data || result.DATA || [];
    if (success) {
      return data.map(normalizeParticipant);
    }
    return [];
  } catch (error) {
    console.error("Error fetching participants:", error);
    return [];
  }
};
var joinEvent = async (eventId, peopleId) => {
  try {
    const result = await apiPost(`${API_ENDPOINTS.EVENTS}/${eventId}/join`, { peopleId });
    const resultObj = result;
    return {
      success: resultObj.success || resultObj.SUCCESS,
      message: resultObj.message || resultObj.MESSAGE || ""
    };
  } catch (error) {
    console.error("Error joining event:", error);
    return { success: false, message: error instanceof Error ? error.message : "Failed to join event" };
  }
};
var leaveEvent = async (eventId, peopleId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_ENDPOINTS.EVENTS}/${eventId}/leave?peopleId=${peopleId}`, {
      method: "DELETE",
      headers
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const result = await response.json();
    return {
      success: result.success || result.SUCCESS,
      message: result.message || result.MESSAGE || ""
    };
  } catch (error) {
    console.error("Error leaving event:", error);
    return { success: false, message: error instanceof Error ? error.message : "Failed to leave event" };
  }
};
var isParticipating = async (eventId, peopleId) => {
  try {
    const result = await apiGet(`${API_ENDPOINTS.EVENTS}/${eventId}/check/${peopleId}`);
    const resultObj = result;
    return resultObj.isParticipating || resultObj.ISPARTICIPATING || false;
  } catch (error) {
    console.error("Error checking participation:", error);
    return false;
  }
};
var addEventParticipant = async (eventId, peopleId, status = "invited") => {
  try {
    const result = await apiPost(`${API_ENDPOINTS.EVENTS}/participants`, {
      eventId,
      peopleId,
      status
    });
    const resultObj = result;
    return {
      success: resultObj.success || resultObj.SUCCESS,
      message: resultObj.message || resultObj.MESSAGE || ""
    };
  } catch (error) {
    console.error("Error adding event participant:", error);
    throw error;
  }
};
var updateEventParticipantStatus = async (eventId, peopleId, status) => {
  try {
    const result = await apiPut(
      `${API_ENDPOINTS.EVENTS}/participants/${eventId}/${peopleId}`,
      { status }
    );
    const resultObj = result;
    return {
      success: resultObj.success || resultObj.SUCCESS,
      message: resultObj.message || resultObj.MESSAGE || ""
    };
  } catch (error) {
    console.error("Error updating participant status:", error);
    throw error;
  }
};
var removeEventParticipant = async (eventId, peopleId) => {
  try {
    const result = await apiDelete(
      `${API_ENDPOINTS.EVENTS}/participants/${eventId}/${peopleId}`
    );
    const resultObj = result;
    return {
      success: resultObj.success || resultObj.SUCCESS,
      message: resultObj.message || resultObj.MESSAGE || ""
    };
  } catch (error) {
    console.error("Error removing event participant:", error);
    throw error;
  }
};
var getEventGroups = async (eventId) => {
  try {
    const result = await apiGet(`${API_ENDPOINTS.EVENTS}/groups/${eventId}`);
    const data = result.data || result.DATA || [];
    return data.map(normalizeGroup);
  } catch (error) {
    console.error("Error getting event groups:", error);
    return [];
  }
};
var addEventGroup = async (eventId, groupId) => {
  try {
    const result = await apiPost(`${API_ENDPOINTS.EVENTS}/groups`, {
      eventId,
      groupId
    });
    const resultObj = result;
    const data = result.data || result.DATA || {};
    return {
      success: resultObj.success || resultObj.SUCCESS,
      id: data.id || data.ID,
      membersAdded: data.membersAdded || data.MEMBERSADDED || 0,
      message: resultObj.message || resultObj.MESSAGE
    };
  } catch (error) {
    console.error("Error adding event group:", error);
    throw error;
  }
};
var removeEventGroup = async (eventId, groupId) => {
  try {
    const result = await apiDelete(
      `${API_ENDPOINTS.EVENTS}/groups/${eventId}/${groupId}`
    );
    const resultObj = result;
    return {
      success: resultObj.success || resultObj.SUCCESS,
      message: resultObj.message || resultObj.MESSAGE
    };
  } catch (error) {
    console.error("Error removing event group:", error);
    throw error;
  }
};
export {
  API_BASE_URL,
  API_ENDPOINTS,
  API_KEY,
  EUROPEAN_COUNTRIES,
  addEventGroup,
  addEventParticipant,
  apiDelete,
  apiGet,
  apiPost,
  apiPut,
  apiRequest,
  createEvent,
  createPeople,
  createPerson,
  deleteEvent,
  deletePeople,
  deletePerson,
  extractCountryFromPlace,
  forgotCode,
  getAllPeople,
  getApiKey,
  getAuthHeaders,
  getAuthToken,
  getCurrentUser,
  getEventById,
  getEventByUuid,
  getEventGroups,
  getEventParticipants,
  getEvents,
  getPeopleByUuid,
  getPeoplePreferences,
  getPersonById,
  getPersonByUuid,
  getProfileStats,
  getStorage,
  getValue,
  handleResponse,
  isAuthenticated,
  isParticipating,
  joinEvent,
  leaveEvent,
  login,
  logout,
  normalizeKeys,
  register,
  removeAuthToken,
  removeEventGroup,
  removeEventParticipant,
  savePeoplePreferences,
  savePerson,
  setAuthToken,
  setStorageAdapter,
  toCamelCase,
  updateEvent,
  updateEventParticipantStatus,
  updatePeople,
  updatePeopleCoordinates,
  updatePerson,
  updatePersonCoordinates
};
